(() => {
  'use strict';

  if (window.InsiemeOpeningSequence) return;
  window.InsiemeOpeningSequence = true;

  class OpeningSequence {
    constructor(root) {
      if (!root || root.dataset.openingReady === 'true') return;

      this.root = root;
      this.viewport = root.querySelector('[data-opening-viewport]');
      this.steps = Array.from(root.querySelectorAll('[data-opening-step]'));
      this.prevButton = root.querySelector('[data-opening-prev]');
      this.nextButton = root.querySelector('[data-opening-next]');
      this.dots = Array.from(root.querySelectorAll('[data-opening-dot]'));
      this.liveRegion = root.querySelector('[data-opening-live]');
      this.activeIndex = Math.max(0, this.steps.findIndex((step) => step.classList.contains('is-active')));
      this.loop = root.dataset.loop === 'true';
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(2000, Number(root.dataset.autoplayDelay || 5000));
      this.transitionDuration = this.readTransitionDuration();
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.transitionTimer = null;
      this.autoplayTimer = null;
      this.isVisible = false;
      this.userInteracted = false;

      if (!this.viewport || this.steps.length === 0) return;

      this.root.dataset.openingReady = 'true';
      this.bindEvents();
      this.goTo(this.activeIndex, false);
      this.setupVisibility();
    }

    readTransitionDuration() {
      const raw = getComputedStyle(this.root).getPropertyValue('--io-transition').trim();
      if (raw.endsWith('ms')) return Math.max(0, Number.parseFloat(raw));
      if (raw.endsWith('s')) return Math.max(0, Number.parseFloat(raw) * 1000);
      return 520;
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(this.activeIndex - 1);
      });

      this.nextButton?.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(this.activeIndex + 1);
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(Number(dot.dataset.openingDot));
        });
      });

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.stopAutoplay();
        this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button')) return;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
      });

      this.viewport.addEventListener('pointerup', (event) => {
        if (this.pointerStartX === null || this.pointerStartY === null) return;

        const deltaX = event.clientX - this.pointerStartX;
        const deltaY = event.clientY - this.pointerStartY;
        this.pointerStartX = null;
        this.pointerStartY = null;

        if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.stopAutoplay();
        this.goTo(this.activeIndex + (deltaX < 0 ? 1 : -1));
      });

      this.viewport.addEventListener('pointercancel', () => {
        this.pointerStartX = null;
        this.pointerStartY = null;
      });

      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
    }

    normalizedIndex(index) {
      const total = this.steps.length;
      if (total <= 0) return 0;
      if (this.loop) return ((index % total) + total) % total;
      return Math.max(0, Math.min(total - 1, index));
    }

    goTo(index, announce = true) {
      const nextIndex = this.normalizedIndex(Number(index) || 0);
      const previousIndex = this.activeIndex;
      const previous = this.steps[previousIndex];
      const next = this.steps[nextIndex];

      window.clearTimeout(this.transitionTimer);

      if (nextIndex !== previousIndex) {
        next.hidden = false;
        next.classList.remove('is-leaving');
        next.setAttribute('aria-hidden', 'false');

        window.requestAnimationFrame(() => {
          previous?.classList.remove('is-active');
          previous?.classList.add('is-leaving');
          previous?.setAttribute('aria-hidden', 'true');
          next.classList.add('is-active');
        });

        this.transitionTimer = window.setTimeout(() => {
          this.steps.forEach((step, stepIndex) => {
            if (stepIndex === nextIndex) return;
            step.classList.remove('is-active', 'is-leaving');
            step.hidden = true;
            step.setAttribute('aria-hidden', 'true');
          });
        }, this.transitionDuration + 40);
      } else {
        this.steps.forEach((step, stepIndex) => {
          const active = stepIndex === nextIndex;
          step.classList.toggle('is-active', active);
          step.classList.remove('is-leaving');
          step.hidden = !active;
          step.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }

      this.activeIndex = nextIndex;
      this.updateControls();

      if (announce && this.liveRegion) {
        const title = next.querySelector('h3')?.textContent?.trim();
        this.liveRegion.textContent = `Passaggio ${nextIndex + 1} di ${this.steps.length}${title ? `: ${title}` : ''}`;
      }

      this.scheduleAutoplay();
    }

    updateControls() {
      this.dots.forEach((dot, index) => {
        const active = index === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (!this.loop) {
        if (this.prevButton) this.prevButton.disabled = this.activeIndex === 0;
        if (this.nextButton) this.nextButton.disabled = this.activeIndex === this.steps.length - 1;
      }
    }

    setupVisibility() {
      if (!this.autoplayEnabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (!('IntersectionObserver' in window)) {
        this.isVisible = true;
        this.scheduleAutoplay();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        this.isVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .45);
        if (this.isVisible) this.scheduleAutoplay();
        else window.clearTimeout(this.autoplayTimer);
      }, { threshold: [.45] });

      this.observer.observe(this.root);
    }

    scheduleAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      if (!this.autoplayEnabled || !this.isVisible || this.userInteracted || this.steps.length < 2) return;

      if (!this.loop && this.activeIndex === this.steps.length - 1) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.goTo(this.activeIndex + 1, false);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      this.userInteracted = true;
      window.clearTimeout(this.autoplayTimer);
    }

    selectBlock(blockId) {
      const index = this.steps.findIndex((step) => step.dataset.shopifyEditorBlock === blockId);
      if (index < 0) return;
      this.stopAutoplay();
      this.goTo(index, false);
    }
  }

  const instances = new WeakMap();

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-insieme-opening]').forEach((root) => {
      const instance = new OpeningSequence(root);
      if (root.dataset.openingReady === 'true') instances.set(root, instance);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-insieme-opening]');
    if (!root) return;
    const instance = instances.get(root);
    const index = instance?.steps.indexOf(event.target);
    if (index >= 0) {
      instance.stopAutoplay();
      instance.goTo(index, false);
    }
  });
})();
